import { Pause, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { WhatsAppAudioExample } from '../../../data/portfolio-examples';
import { hasMedia } from '../../../data/portfolio-examples';
import { formatTime } from '../playback';
import EmptyMediaSlot from './EmptyMediaSlot';

type Props = {
  example: WhatsAppAudioExample;
  registerMedia: (id: string, element: HTMLMediaElement | null) => void;
  claimPlayback: (id: string) => void;
};

// Alturas fixas: a onda é decorativa e não deve mudar a cada render.
const WAVE = [
  32, 54, 41, 68, 86, 62, 45, 74, 96, 70, 52, 38, 60, 84, 100, 76, 58, 44, 66, 90,
  72, 50, 36, 62, 88, 68, 46, 58, 80, 54, 40, 64, 92, 70, 48, 34, 56, 78, 60, 42,
];

/**
 * Retrato da entrega do essencial: a música chega como mensagem de voz no
 * WhatsApp, sem página, sem letra e sem vídeo.
 */
export default function WhatsAppAudioCard({ example, registerMedia, claimPlayback }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const ready = hasMedia(example.audio);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setElapsed(audio.currentTime);
    const onMeta = () => setDuration(audio.duration);
    const onEnd = () => {
      setPlaying(false);
      setElapsed(0);
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnd);
    };
  }, [ready]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) void audio.play().catch(() => {});
    else audio.pause();
  };

  const total = duration || 0;
  const progress = total > 0 ? elapsed / total : 0;
  const playedBars = Math.round(progress * WAVE.length);
  const timeLabel = ready
    ? playing || elapsed > 0
      ? formatTime(elapsed)
      : example.audio.duration || formatTime(total)
    : '--:--';

  return (
    <article className="ex-sample">
      <div className="ex-wa">
        <div className="ex-wa__bar">
          <span className="ex-wa__bar-avatar" aria-hidden="true">IM</span>
          <span className="ex-wa__bar-name">Inspira Músicas</span>
        </div>

        <div className="ex-wa__thread">
          <div className="ex-wa__bubble">
            <div className="ex-wa__voice">
              <button
                type="button"
                className="ex-wa__play"
                onClick={toggle}
                disabled={!ready}
                aria-label={playing ? `Pausar o exemplo de ${example.genre}` : `Ouvir o exemplo de ${example.genre}, ${example.occasion}`}
              >
                {playing ? (
                  <Pause size={22} fill="currentColor" strokeWidth={0} aria-hidden="true" />
                ) : (
                  <Play size={22} fill="currentColor" strokeWidth={0} aria-hidden="true" />
                )}
              </button>

              <div className="ex-wa__wave" aria-hidden="true">
                {WAVE.map((height, index) => (
                  <span
                    key={index}
                    data-played={index < playedBars}
                    style={{ height: `${Math.max(12, height) * 0.24}px` }}
                  />
                ))}
              </div>

              <span className="ex-wa__avatar">
                <img src="/inspira-favicon.png" alt="" />
              </span>
            </div>

            <p className="ex-wa__meta">
              <span>{timeLabel}</span>
              <span>20:14</span>
            </p>
          </div>

          {ready && (
            <audio
              ref={(element) => {
                audioRef.current = element;
                registerMedia(example.id, element);
              }}
              src={example.audio.src}
              preload="none"
              onPlay={() => {
                claimPlayback(example.id);
                setPlaying(true);
              }}
              onPause={() => setPlaying(false)}
            />
          )}
        </div>
      </div>

      {!ready && <EmptyMediaSlot tone="light" inline spec="MP3 da música completa, o mesmo arquivo que vai pro chat" />}

      <div className="ex-card__meta">
        <span className="ex-card__genre">{example.genre}</span>
        <p className="ex-card__occasion">{example.occasion}</p>
        <p className="ex-card__plan">Áudio no WhatsApp · feita para {example.recipient}</p>
        {ready && example.audio.duration && (
          <p className="ex-card__duration">Música inteira, {example.audio.duration}</p>
        )}
      </div>
    </article>
  );
}
